import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, Observable, isObservable } from 'rxjs';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { JobPointeService } from 'src/app/service/job-pointe/job-pointe.service';

import { SelectedDataModel, TransferModel, TransferModelData } from './job-pointe-transfer.model';
import { LoaderService } from 'src/app/service/shared/loader/loader.service';
import { JobBoardService } from '../job-board/service/job-board.service';
import { SweetAlertService } from 'src/app/service/shared/toast/sweet-alert.service';

@Injectable({
  providedIn: 'root',
})
export class JobPointeTransferService {
  public selectedItemsSource = new BehaviorSubject<Array<SelectedDataModel>>([]);
  selectedItems = this.selectedItemsSource.asObservable();

  selectedRowSource = new BehaviorSubject<any>({});
  selectedRow = this.selectedRowSource.asObservable();

  modalIsOpenSource = new BehaviorSubject<boolean>(false);
  modalIsOpen = this.modalIsOpenSource.asObservable();

  jobCardModalIsOpenSource = new BehaviorSubject<boolean>(false);
  isJobCardModalOpen = this.jobCardModalIsOpenSource.asObservable();

  manageOwnerModalIsOpenSource = new BehaviorSubject<boolean>(false);
  isManageOwnerModalOpen = this.manageOwnerModalIsOpenSource.asObservable();

  addEmployeeModalIsOpenSource = new BehaviorSubject<any>(false);
  isAddToEmployeeModalOpen = this.addEmployeeModalIsOpenSource.asObservable();

  editEmployeeModalIsOpenSource = new BehaviorSubject<boolean>(false);
  isEditEmployeeModalOpen = this.editEmployeeModalIsOpenSource.asObservable();

  private submitSubscriberSource = new BehaviorSubject<any>({
    next: null,
    error: null,
  });
  submitSubscriber = this.submitSubscriberSource.asObservable();

  private refreshToolsSubscriberSource = new BehaviorSubject<any>({
    next: null,
    error: null,
  });
  refreshToolsSubscriber = this.refreshToolsSubscriberSource.asObservable();

  defaultTransferModel: TransferModel = {
    toolId: null,
    assignedDate: null,
    notes: null,
  };

  transferModel: TransferModel = this.defaultTransferModel;
  transferSubscription: Subscription;
  refreshData: Function;

  constructor(
    private jobPointeService: JobPointeService,
    private toastService: ToastService,
    private loaderService: LoaderService,
    private jobBoardService: JobBoardService,
    private sweetAlertService: SweetAlertService
  ) { }

  transfer(item: SelectedDataModel, refreshSubscription: Subscription = undefined) {
    this.transferSubscription = refreshSubscription;
    this.modalIsOpenSource.next(true);
    this.add(item);
  }

  batchTransfer(refreshSubscription: Subscription = undefined) {
    this.transferSubscription = refreshSubscription;
    this.modalIsOpenSource.next(true);
  }

  batchAdd(items: Array<SelectedDataModel>) {
    const currentList = this.selectedItemsSource.value;
    let updatedList = [...currentList, ...items];
    updatedList = this.filterDuplicates(updatedList);
    this.selectedItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  batchRemove(items: Array<SelectedDataModel>) {
    const currentList = this.selectedItemsSource.value;
    let updatedList = this.filterRemovable(currentList, items);
    this.selectedItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }


  async addEmployeeToCard(employee, card, showLoader = false, updateBoard = true) {
    const messages = [];

    const cardUnionLocal = card.potentialJobDetail?.unionLocal?.unionLocalId ?? card.jobDetail?.unionLocal?.unionLocalId

    if (employee.employeeUnionLocal?.name !== cardUnionLocal) {
      messages.push(`<p>⚠️ <b>Union Mismatch:</b> The employee you are trying to assign is associated with a different Union than the Site Local of the Job.</p>`);
    }

    const jobDetail = card.potentialJobDetail || card.jobDetail;
    const title = employee?.employeeTitle?.name?.trim().toLowerCase();

    if (
      Boolean(jobDetail?.laborPrevailingWage) &&
      title &&
      !['foreman', 'journeyman'].includes(title)
    ) {
      messages.push(`<p>⚠️ <b>Prevailing Wage Warning:</b> You are assigning an employee to a job marked as Prevailing Wage.</p>`);
    }

    let data = {
      employeeId: employee.id,
      facilityId: card?.facilityName ? card.id : null,
      jobId: card?.jobNumber ? card.id : null,
    };

    let result = await this.jobPointeService.create['employeeCardSchedule'](data).toPromise();

    // Certification Mismatch Check
    if (result?.data && Array.isArray(result.data)) {

      const missingCerts = [...new Set(result.data.map(cert => cert.name))];

      if (missingCerts.length > 0) {
        const certList = missingCerts.map(cert => `<li>${cert}</li>`).join('');
        messages.push(`
                  <p style="text-align: left">⚠️ <b>Certification Mismatch:</b> This job requires certifications that the selected employee doesn't have:</p>
                  <ul style="margin-top: 5px; margin-bottom: 5px; text-align: left">${certList}</ul>
                  <p>Please update the employee's certifications or assign a different employee.</p>
              `);
      }
    }

    // Show all warnings together
    if (messages.length > 0) {
      this.sweetAlertService.showHtmlMessage(
        'Assignment Warnings',
        messages.join('<hr>'),
        'warning'
      );
    }

    // Update board
    if (updateBoard) await this.jobBoardService.updateJobBoard(showLoader);
    return result;
  }


  async moveEmployeeToCard(employee, fromCard, toCard, updateBoard = true) {
    let data = {
      from: {
        employeeId: employee.id,
        facilityId: fromCard?.facilityName ? fromCard.id : null,
        jobId: fromCard?.jobNumber ? fromCard.id : null,
      },
      to: {
        facilityId: toCard?.facilityName ? toCard.id : null,
        jobId: toCard?.jobNumber ? toCard.id : null,
      },
    };
    let result = await this.jobPointeService.update['employeeCardSchedule'](data).toPromise();
    if (updateBoard) await this.jobBoardService.updateJobBoard(true);
    return result;
  }

  async updateJobBoardStatus(job, newStatus) {
    if (job?.scheduleEmployees?.length && newStatus === 'Inactive') {
      this.toastService.showToast(
        `Job # ${job.jobNumber} cannot be deactivated while it still has employees assigned to it. Please remove all employees before removing this job`,
        'error',
      );
      return false;
    }
    if (newStatus === 'Inactive') {
      await (await this.toggleJobOnJobBoard(job, false)).toPromise();
      await (await this.toggleJobSeparateOnJobBoard(job, false)).toPromise();
    }
    return this.jobPointeService.update['jobs']([
      {
        job_number: job.jobNumber,
        name: job.name,
        status: job.status,
        job_board_status: newStatus,
      },
    ]);
  }

  async toggleJobOnJobBoard(job, isOnJobBoard = !job.isOnJobBoard): Promise<Observable<any>> {
    let toggle = (): Observable<any> => {
      const { jobNumber, status, name } = job;
      return this.jobPointeService.update['jobs']([
        {
          job_number: jobNumber,
          name: name,
          status: status,
          is_on_job_board: isOnJobBoard,
          ...(isOnJobBoard && { last_on_job_board_date: new Date() }),
          job_board_status: isOnJobBoard || job.isSeparateOnJobBoard ? 'Active' : 'Inactive', //set status if added/removed from job board
        },
      ]);
    };
    if (job.jobBoardStatus == 'Inactive' && isOnJobBoard) {
      let result = await this.toastService.showToast(
        `You are about to assign an Inactive Job (${job.jobNumber}) to the Job Board. Would you like to proceed?`,
        'warningConfirmation',
        toggle.bind(this, job),
      );
      return result;
    }
    return toggle();
  }
  async toggleJobSeparateOnJobBoard(job, isSeparateOnJobBoard = !job.isSeparateOnJobBoard): Promise<Observable<any>> {
    let toggle = (): Observable<any> => {
      const { jobNumber, status, name } = job;
      return this.jobPointeService.update['jobs']([
        {
          job_number: jobNumber,
          name: name,
          status: status,
          ...(isSeparateOnJobBoard && { last_on_job_board_date: new Date() }),
          is_separate_on_job_board: isSeparateOnJobBoard,
          job_board_status: isSeparateOnJobBoard || job.isOnJobBoard ? 'Active' : 'Inactive', //set status if added/removed from job board
        },
      ]);
    };
    if (job.jobBoardStatus == 'Inactive' && isSeparateOnJobBoard) {
      let result = await this.toastService.showToast(
        `You are about to assign an Inactive Job (${job.jobNumber}) to the Job Board. Would you like to proceed?`,
        'warningConfirmation',
        toggle.bind(this, job),
      );
      return result;
    }
    return toggle();
  }

  async batchUpdateJobBoardStatus(jobs, newStatus) {
    let promises = [];
    for (let job of jobs) {
      promises.push(
        new Promise(async (resolve, reject) => {
          let result = await this.updateJobBoardStatus(job, newStatus);
          if (!isObservable(result)) return resolve(result);
          result.subscribe(
            (res: any) => {
              if (res?.batchResponse.success.length) {
                resolve(res);
              }
              resolve(res);
            },
            err => {
              this.toastService.showToast('Error updating job status', 'error');
              reject(err);
            },
          );
        }),
      );
    }
    this.clearSelectedItems();
    return Promise.all(promises);
  }
  async batchAddJobsToJobBoard(jobs) {
    let promises = [];
    for (let job of jobs) {
      promises.push(
        new Promise(async (resolve, reject) => {
          let result = await this.toggleJobOnJobBoard(job, true);
          if (!isObservable(result)) return resolve(result);
          result.subscribe(
            res => {
              if (res) {
                resolve(res);
              }
              reject(res);
            },
            err => {
              this.toastService.showToast('Error adding job to job board', 'error');
              reject(err);
            },
          );
        }),
      );
    }
    this.clearSelectedItems();
    return await Promise.all(promises);
  }

  add(item: SelectedDataModel, promisfy: boolean = false) { }

  get(id: number) {
    const currentList = this.selectedItemsSource.value;
    return currentList.find(row => {
      return row.id === id;
    });
  }

  remove(id: number) {
    const currentList = this.selectedItemsSource.value;
    const updatedList = currentList.filter(row => {
      return row.id !== id;
    });
    this.selectedItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  clearSelectedItems() {
    // this.clearModelData();
    this.selectedItemsSource.next([]);
    this.saveToLocalStorage();
  }

  saveToLocalStorage() {
    localStorage.setItem('selected-items', JSON.stringify(this.selectedItemsSource.value));
  }

  loadFromLocalStorage() {
    const data = JSON.parse(localStorage.getItem('selected-items')) || [];
    this.selectedItemsSource.next(data);
  }

  filterDuplicates(list: Array<SelectedDataModel>) {
    return list.filter((e, i) => list.findIndex(obj => obj.id == e.id) === i);
  }

  filterRemovable(a: Array<SelectedDataModel>, b: Array<SelectedDataModel>) {
    return a.filter(e => !b.find(f => f.id === e.id));
  }

  async submitTransfer(e) {
    if (!this.getIds().length) {
      return this.toastService.showToast('Select at least one tool first', 'error');
    }
  }

  handleSuccess(next, e) {
    this.refreshToolsSubscriberSource.next({ next });
    this.toastService.showToast('Tools transferred successfully', 'success');
    this.clearSelectedItems();
    if (e === 'close') {
      this.close();
    }
    this.submitSubscriberSource.next({ next });
  }

  handleError(err) {
    this.toastService.showToast('Something went wrong', 'error');
    this.submitSubscriberSource.next({ err });
  }

  addModelData(model: TransferModelData) {
    const { key, value } = model;
    this.transferModel[key] = value;
  }

  removeModelData(model: TransferModelData) {
    const { key, value } = model;
    delete this.transferModel[key];
  }

  clearModelData() {
    const { assignedDate } = this.transferModel;
    this.transferModel = { ...this.defaultTransferModel };
    this.transferModel.assignedDate = assignedDate;
  }

  getModelData() {
    return this.getIds().map((id: number) => {
      return {
        ...this.transferModel,
        toolId: id,
      };
    });
  }

  getIds() {
    return this.selectedItemsSource.value.map((item: SelectedDataModel) => item.id);
  }

  close() {
    if (this.transferSubscription) this.transferSubscription.unsubscribe();
    this.modalIsOpenSource.next(false);
    this.jobCardModalIsOpenSource.next(false);
    this.manageOwnerModalIsOpenSource.next(false);
    this.addEmployeeModalIsOpenSource.next(false);
    this.editEmployeeModalIsOpenSource.next(false);
  }
}
